// pages/VideoPage.jsx — Anamoria SPA
// v1.5 — 4.3 relabel: "Remove/Keep" register, "trash" retired (July 24, 2026)
//   Copy v0.12 §1a (D-C7). Copy-only: affordance + DeleteConfirmModal strings.
// v1.4 — Delete video memory from edit view (June 23, 2026)
//
// Changes from v1.3:
//   - Edit screen now has a "Delete memory" action mirroring MemoryDetailPage
//     v2.7: DeleteConfirmModal -> POST /memories/:id/delete (soft-delete to
//     trash), invalidate feed cache, return to feed. Video memories open here
//     (not MemoryDetailPage), so they had no delete control.
//
// v1.3 — Send prompt text unconditionally + groupPromptId (June 23, 2026)
//
// Changes from v1.2:
//   - handleSave POST body: promptText/promptTitle are now sent whenever a
//     prompt is present (promptId is null for custom group prompts under
//     prompts v1.3, so the old activePromptId gate would have dropped them).
//   - NEW: sends groupPromptId when present (memories.group_prompt_id,
//     anamoria-memories v2.13).
//
// v1.2 — Custom video player (May 16, 2026)
//
// Changes from v1.1:
//   - Review screen: replaced native <video controls> with custom player
//     matching RecordPage's audio player pattern (sage play button,
//     progress bar, duration display). Video thumbnail click toggles
//     play/pause with semi-transparent overlay play icon.
//   - Success screen: video thumbnail with play icon overlay, no native
//     controls (matches warm visual language).
//   - Added videoPlaying, videoProgress, videoCurrentTime state for
//     custom playback control.
//   - Added toggleVideoPlayback, handleProgressClick handlers.
//
// Changes from v1.0:
//   - Pre-flight idle screen: replaced dark black-box placeholder with
//     sage circle containing video camera icon (matches RecordPage's
//     red circle pattern adapted for video). Warm cream background.
//   - Camera-active state: video preview in warm-bordered container
//     instead of stark dark box.
//   - Two-phase layout: idle shows circle + "Choose from Library" below;
//     camera-active shows preview + controls. Start recording triggers
//     camera acquisition and transitions to the camera-active layout.
//   - All review/save/success/upload screens unchanged from v1.0.
//
// Two entry paths:
//   1. Record — camera capture via useVideoRecorder hook
//   2. Library — file picker for existing video (.mp4, .webm, .mov)
//
// Sub-screens (render order matches RecordPage pattern):
//   1. Loading — space + prompt data
//   2. Uploading — S3 upload + memory creation
//   3. Success — shared <SuccessScreen> with video thumbnail preview
//   4. Review/Save — video playback + caption + privacy + save CTA
//   5. Pre-flight + Recording — camera preview, controls, library picker
//
// Route: /spaces/:spaceId/video (protected — JWT required)
// Components: LovedOneBar, PromptBanner (fullWidth), BottomNav,
//             SuccessScreen, BrandIcons.VideoIcon, LeaveConfirmModal

import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useVideoRecorder } from '../hooks/useVideoRecorder';
import { useResolvedPhotoUrl } from '../hooks/useResolvedPhotoUrl';
import { createApiClient } from '../api/client';
import LovedOneBar from '../components/LovedOneBar';
import PromptBanner from '../components/PromptBanner';
import BottomNav from '../components/BottomNav';
import SuccessScreen from '../components/SuccessScreen';
import { invalidateMemoriesCache } from '../components/MemoryFeed';
import { VideoIcon } from '../components/BrandIcons';
import LeaveConfirmModal from '../components/LeaveConfirmModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import { checkPostSaveGating } from '../utils/postSaveGating';
import styles from './VideoPage.module.css';

/* ═══════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════ */

// Accepted video MIME types for library upload
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const ACCEPTED_VIDEO_EXTENSIONS = '.mp4,.webm,.mov';
// File size cap — 100 MB (Decision #8)
const MAX_FILE_SIZE = 100 * 1024 * 1024;
// Duration cap — 5 minutes (Decision #6 amended)
const MAX_DURATION_SEC = 300;

/* ═══════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════ */

function formatDurationSec(val) {
  const s = Math.round(Number(val) || 0);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

/** Validate a user-selected video file. Returns { valid, error, duration }. */
function validateVideoFile(file) {
  return new Promise((resolve) => {
    // Type check
    if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) {
      resolve({ valid: false, error: 'Unsupported file type. Please choose an MP4, WebM, or MOV video.' });
      return;
    }
    // Size check
    if (file.size > MAX_FILE_SIZE) {
      resolve({ valid: false, error: 'File is too large. Maximum size is 100 MB.' });
      return;
    }
    // Duration check — load into a temp <video> element
    const video = document.createElement('video');
    video.preload = 'metadata';
    const url = URL.createObjectURL(file);
    video.src = url;

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      if (video.duration > MAX_DURATION_SEC) {
        resolve({
          valid: false,
          error: `Video is too long (${formatDurationSec(video.duration)}). Maximum is ${formatDurationSec(MAX_DURATION_SEC)}.`,
        });
      } else {
        resolve({ valid: true, error: null, duration: Math.round(video.duration) });
      }
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ valid: false, error: 'Could not read this video file. Please try a different file.' });
    };
  });
}

/* ═══════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════ */

export default function VideoPage() {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessTokenSilently } = useAuth0();

  const getApi = useCallback(
    () => createApiClient(getAccessTokenSilently),
    [getAccessTokenSilently]
  );

  // ─── Edit mode (matches RecordPage v5.0 pattern) ─────────
  const editMode = location.state?.editMode || false;
  const editMemory = location.state?.editMemory || null;

  const {
    recordingState,
    videoBlob,
    mimeType,
    duration,
    durationFormatted,
    byteCount,
    atWarning,
    error: recorderError,
    previewStream,
    facingMode,
    permissionState,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    reRecord,
    toggleCamera,
    checkPermission,
  } = useVideoRecorder();

  // ─── Space + Prompt data ─────────────────────────────────

  const [space, setSpace] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingData(true);
      try {
        const api = getApi();
        const [spaceData, promptData] = await Promise.all([
          api.get(`/spaces/${spaceId}`),
          api.get(`/spaces/${spaceId}/prompt`).catch(() => null),
        ]);
        if (!cancelled) {
          setSpace(spaceData);
          setPrompt(promptData);
        }
      } catch (err) {
        console.error('VideoPage data load error:', err);
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [spaceId, getApi]);

  const activePromptId = prompt?.promptId || null;

  // Check camera permission on mount (BPR §1.11)
  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  // ─── Edit mode: fetch existing video URL + pre-fill ──────

  const [existingVideoUrl, setExistingVideoUrl] = useState(null);
  const [existingPosterUrl, setExistingPosterUrl] = useState(null);
  const [loadingEditVideo, setLoadingEditVideo] = useState(!!editMode);

  useEffect(() => {
    if (!editMode || !editMemory) {
      setLoadingEditVideo(false);
      return;
    }

    // Pre-fill label and privacy from existing memory
    setMemoryLabel(editMemory.title || '');
    setIsPrivate(editMemory.isPrivate ?? true);

    // Use signed URLs from the memory object (already in feed response)
    const playbackUrl = editMemory.videoNote?.playbackUrl || null;
    const posterUrl = editMemory.videoNote?.posterUrl || null;

    if (playbackUrl) setExistingVideoUrl(playbackUrl);
    if (posterUrl) setExistingPosterUrl(posterUrl);
    setLoadingEditVideo(false);
  }, [editMode, editMemory]);

  // ─── Review screen state ─────────────────────────────────

  const [isPrivate, setIsPrivate] = useState(editMemory?.isPrivate ?? true);
  const [memoryLabel, setMemoryLabel] = useState(editMemory?.title || '');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [saved, setSaved] = useState(false);
  const [savedDuration, setSavedDuration] = useState('0:00');
  const [savedMemoryId, setSavedMemoryId] = useState(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  // v1.4: delete-memory modal state (edit view).
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ─── Library upload state ────────────────────────────────

  const [libraryFile, setLibraryFile] = useState(null);
  const [libraryBlob, setLibraryBlob] = useState(null);
  const [libraryDuration, setLibraryDuration] = useState(0);
  const [libraryMimeType, setLibraryMimeType] = useState('');
  const [libraryError, setLibraryError] = useState('');
  const fileInputRef = useRef(null);

  // ─── Video preview refs ──────────────────────────────────

  const liveVideoRef = useRef(null);
  const reviewVideoRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Attach live stream to <video> element for camera preview
  useEffect(() => {
    if (liveVideoRef.current && previewStream) {
      liveVideoRef.current.srcObject = previewStream;
    }
  }, [previewStream]);

  // Create blob URL for review screen playback
  useEffect(() => {
    const blob = videoBlob || libraryBlob;
    if (blob) {
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [videoBlob, libraryBlob]);

  // Force first-frame display: seek to 0.1s once metadata loads.
  // Without this, <video> shows black until the user hits play.
  // Industry standard trick — forces the browser to decode a frame.
  useEffect(() => {
    const video = reviewVideoRef.current;
    if (!video || !previewUrl) return;

    function seekToFirstFrame() {
      if (video.duration && video.currentTime === 0) {
        video.currentTime = 0.1;
      }
    }
    video.addEventListener('loadeddata', seekToFirstFrame);
    return () => video.removeEventListener('loadeddata', seekToFirstFrame);
  }, [previewUrl]);

  // ─── Custom video player state (review + success screens) ──

  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoCurrentTime, setVideoCurrentTime] = useState('0:00');

  // Attach time update listener to review video element
  useEffect(() => {
    const video = reviewVideoRef.current;
    if (!video) return;

    function handleTimeUpdate() {
      if (video.duration) {
        setVideoProgress((video.currentTime / video.duration) * 100);
        setVideoCurrentTime(formatDurationSec(video.currentTime));
      }
    }
    function handleEnded() {
      setVideoPlaying(false);
      setVideoProgress(0);
      setVideoCurrentTime('0:00');
    }
    function handlePause() {
      setVideoPlaying(false);
    }
    function handlePlay() {
      setVideoPlaying(true);
    }

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('pause', handlePause);
    video.addEventListener('play', handlePlay);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('play', handlePlay);
    };
  });

  function toggleVideoPlayback() {
    const video = reviewVideoRef.current;
    if (!video) return;
    if (videoPlaying) { video.pause(); } else { video.play(); }
  }

  function handleProgressClick(e) {
    const video = reviewVideoRef.current;
    if (!video || !video.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    video.currentTime = ratio * video.duration;
    setVideoProgress(ratio * 100);
    setVideoCurrentTime(formatDurationSec(video.currentTime));
  }

  // Cleanup video playback on unmount
  useEffect(() => {
    return () => {
      if (reviewVideoRef.current) {
        reviewVideoRef.current.pause();
      }
    };
  }, []);

  const activeBlob = videoBlob || libraryBlob;
  const activeDuration = videoBlob ? duration : libraryDuration;
  const activeDurationFormatted = videoBlob ? durationFormatted : formatDurationSec(libraryDuration);
  const activeMimeType = videoBlob ? mimeType : libraryMimeType;

  // ─── Library file picker ─────────────────────────────────

  function handleLibraryClick() {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }

  async function handleFileSelected(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    setLibraryError('');
    const result = await validateVideoFile(file);

    if (!result.valid) {
      setLibraryError(result.error);
      return;
    }

    setLibraryFile(file);
    setLibraryBlob(file);
    setLibraryDuration(result.duration);
    setLibraryMimeType(file.type);
  }

  // ─── Prompt skip ─────────────────────────────────────────

  const handleSkipPrompt = useCallback(async () => {
    try {
      const api = getApi();
      const nextPrompt = await api.post(`/spaces/${spaceId}/prompt/advance`);
      if (nextPrompt) setPrompt(nextPrompt);
    } catch (err) {
      console.error('Skip prompt error:', err);
    }
  }, [getApi, spaceId]);

  // ─── Save handler ────────────────────────────────────────

  async function handleSave() {
    if (!activeBlob) return;
    setUploadError('');
    setUploading(true);

    // For library files, type may be 'video/quicktime' — normalize to base
    const baseMimeType = (activeMimeType || 'video/webm').split(';')[0];

    try {
      const api = getApi();

      // Step 1: Get pre-signed upload URL
      const uploadData = await api.post('/media/upload-url', {
        spaceId,
        mimeType: baseMimeType,
        mediaType: 'video',
      });

      // Step 2: Upload blob to S3
      await api.putS3(uploadData.uploadUrl, activeBlob, baseMimeType);

      // Step 3: Create memory record
      const createdMemory = await api.post(`/spaces/${spaceId}/memories`, {
        type: 'video',
        s3Key: uploadData.s3Key,
        mimeType: baseMimeType,
        duration: Math.round(activeDuration),
        isPrivate,
        ...(memoryLabel.trim() ? { title: memoryLabel.trim() } : {}),
        ...(activePromptId ? { promptId: activePromptId } : {}),
        // v1.3: send prompt text whenever a prompt is present (promptId is null
        // for custom group prompts under prompts v1.3) + group provenance.
        ...(prompt ? {
          promptText: prompt.text || null,
          promptTitle: prompt.title || prompt.category || null,
        } : {}),
        ...(prompt?.groupPromptId ? { groupPromptId: prompt.groupPromptId } : {}),
      });
      setSavedMemoryId(createdMemory.id);

      // Step 4: Mark prompt as responded
      if (activePromptId) {
        api.post(`/spaces/${spaceId}/prompt/respond`, { promptId: activePromptId }).catch(() => {});
      }

      setSavedDuration(activeDurationFormatted);
      setUploading(false);
      setSaved(true);
      invalidateMemoriesCache(spaceId);
    } catch (err) {
      console.error('Video save error:', err);
      setUploadError('Upload failed. Please try again.');
      setUploading(false);
    }
  }

  // ─── Edit save (PATCH — matches RecordPage handleEditSave pattern) ──
  // Edit path never triggers feedback or reminder gating (RG-4, RG-13).

  async function handleEditSave() {
    setUploadError('');
    setUploading(true);

    try {
      const api = getApi();
      const updates = {};

      // Metadata changes
      const trimLabel = memoryLabel.trim();
      if (trimLabel !== (editMemory.title || '')) {
        updates.title = trimLabel || null;
      }
      if (isPrivate !== (editMemory.isPrivate ?? true)) {
        updates.isPrivate = isPrivate;
      }

      // If user re-recorded, upload new video
      if (videoBlob) {
        const baseMimeType = (mimeType || 'video/webm').split(';')[0];
        const uploadData = await api.post('/media/upload-url', {
          spaceId,
          mimeType: baseMimeType,
          mediaType: 'video',
        });
        await api.putS3(uploadData.uploadUrl, videoBlob, baseMimeType);

        updates.newVideoS3Key = uploadData.s3Key;
        updates.newVideoDuration = Math.round(duration);
        updates.newVideoMimeType = baseMimeType;
      }

      // Always send PATCH (even if only metadata)
      if (Object.keys(updates).length > 0) {
        await api.patch(`/memories/${editMemory.id}`, updates);
      }

      // Duration for success screen
      if (videoBlob) {
        setSavedDuration(durationFormatted);
      } else {
        setSavedDuration(formatDurationSec(editMemory.videoNote?.durationSeconds || 0));
      }

      setUploading(false);
      setSaved(true);
      invalidateMemoriesCache(spaceId);
    } catch (err) {
      console.error('Edit save error:', err);
      setUploadError('Save failed. Please try again.');
      setUploading(false);
    }
  }

  // ─── Record/choose another (from success screen) ─────────

  // ─── v1.4: Delete video memory (edit view) ───────────────
  // Mirrors MemoryDetailPage v2.7: soft-delete to trash, then back to feed.
  function handleDelete() {
    setShowDeleteConfirm(true);
  }

  async function handleConfirmDelete() {
    if (deleting || !editMemory) return;
    setDeleting(true);
    setUploadError('');
    try {
      const api = getApi();
      await api.post(`/memories/${editMemory.id}/delete`);
      invalidateMemoriesCache(spaceId);
      navigate(`/spaces/${spaceId}`, { replace: true });
    } catch (err) {
      console.error('Delete error:', err);
      setUploadError("Couldn't delete this memory. Please try again.");
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  }

  function handleRecordAnother() {
    setSaved(false);
    setUploadError('');
    setIsPrivate(true);
    setMemoryLabel('');
    setSavedMemoryId(null);
    setLibraryFile(null);
    setLibraryBlob(null);
    setLibraryDuration(0);
    setLibraryMimeType('');
    setLibraryError('');
    reRecord();
  }

  // ─── Re-record / re-choose from review screen ───────────

  function handleReRecordOrChoose() {
    setLibraryFile(null);
    setLibraryBlob(null);
    setLibraryDuration(0);
    setLibraryMimeType('');
    setLibraryError('');
    setUploadError('');
    setMemoryLabel('');
    setIsPrivate(true);
    reRecord();
  }

  // ─── Unsaved work detection ──────────────────────────────

  const hasUnsavedWork =
    recordingState === 'recording' ||
    recordingState === 'paused' ||
    (recordingState === 'stopped' && videoBlob !== null) ||
    libraryBlob !== null;

  const handleBackArrow = useCallback(() => {
    if (hasUnsavedWork) {
      setShowLeaveConfirm(true);
    } else {
      navigate(`/spaces/${spaceId}`, { replace: true });
    }
  }, [hasUnsavedWork, navigate, spaceId]);

  // ─── View all memories (post-save gating) ─────────────────

  const handleViewAllMemories = useCallback(async () => {
    // Edit path: never trigger feedback or reminder gating (RG-4, RG-13)
    if (editMode) {
      navigate(`/spaces/${spaceId}`, { replace: true });
      return;
    }
    try {
      const gate = await checkPostSaveGating({
        spaceId,
        space,
        memoryType: 'video',
        getApi,
      });
      if (gate.redirectTo === 'reminder') {
        navigate(`/spaces/${spaceId}/reminder`);
      } else if (gate.redirectTo === 'feedback') {
        navigate(`/spaces/${spaceId}/feedback`, {
          state: {
            memoryId: savedMemoryId,
            memoryType: 'video',
            triggerContext: gate.triggerContext,
            userMemoryCount: gate.userMemoryCount,
          },
        });
      } else {
        navigate(`/spaces/${spaceId}`, { replace: true });
      }
    } catch (err) {
      console.error('Gating check failed:', err);
      navigate(`/spaces/${spaceId}`, { replace: true });
    }
  }, [spaceId, space, navigate, getApi, savedMemoryId]);

  // ─── Circle tap handler (recording controls) ─────────────

  function handleRecordTap() {
    if (recordingState === 'idle') startRecording();
    else if (recordingState === 'recording') pauseRecording();
    else if (recordingState === 'paused') resumeRecording();
  }

  // ─── Derived values ──────────────────────────────────────

  const spaceName = space?.name || 'Space';
  const spacePhotoUrl = useResolvedPhotoUrl(space, getAccessTokenSilently);

  const hintText =
    recordingState === 'recording' ? 'Tap to pause · 5:00 max' :
    recordingState === 'paused'    ? 'Tap to resume · 5:00 max' :
                                     'Tap the circle to start · 5:00 max';

  const stateLabel =
    recordingState === 'recording' ? 'Recording...' :
    recordingState === 'paused'    ? 'Paused' :
                                     'Ready';

  const barSubtitle =
    (recordingState === 'stopped' && videoBlob) ? 'Review before saving' :
    libraryBlob                                 ? 'Review before saving' :
    recordingState === 'recording'              ? 'Recording...' :
    recordingState === 'paused'                 ? 'Paused' :
                                                  'Record or choose a video';

  // Mirror class for front camera preview (BPR §1.5)
  const mirrorClass = facingMode === 'user' ? styles.mirrored : '';

  // ═══════════════════════════════════════════════════════════
  // RENDER ORDER
  // ═══════════════════════════════════════════════════════════

  // ─── 1. Loading ───────────────────────────────────────────

  if (loadingData) {
    return (
      <div className={styles.loadingPage}>
        <div className="app-loading-spinner" />
      </div>
    );
  }

  // ─── 2. Uploading ─────────────────────────────────────────

  if (uploading) {
    return (
      <div className={styles.page}>
        <LovedOneBar
          spaceName={spaceName}
          spacePhotoUrl={spacePhotoUrl}
          subtitle="Saving..."
          onBack={() => {}}
          backLabel="Saving in progress"
        />
        <div className={styles.uploadingContent}>
          <div className="app-loading-spinner" />
          <p className={styles.uploadingText}>Saving your video...</p>
        </div>
        <BottomNav spaceId={spaceId} activeTab="video" />
      </div>
    );
  }

  // ─── 3. Success screen ───────────────────────────────────

  if (saved) {
    const subtitle = memoryLabel.trim()
      ? `${memoryLabel.trim()} · Memory saved`
      : 'Memory saved';

    return (
      <SuccessScreen
        spaceName={spaceName}
        spacePhotoUrl={spacePhotoUrl}
        subtitle={subtitle}
        onBack={() => navigate(`/spaces/${spaceId}`, { replace: true })}
        backLabel="Back to feed"
        badgeLabel={editMode ? 'UPDATED' : 'JUST ADDED'}
        promptText={prompt?.text || null}
        isPrivate={isPrivate}
        primaryCta={{
          icon: <VideoIcon />,
          label: editMode ? 'Back to feed' : 'Record another video',
          onClick: editMode ? () => navigate(`/spaces/${spaceId}`, { replace: true }) : handleRecordAnother,
        }}
        secondaryCta={{
          label: 'Invite family to add memories',
          onClick: () => navigate(`/spaces/${spaceId}/invite`),
        }}
        tertiaryCta={{
          label: 'View all memories',
          onClick: handleViewAllMemories,
        }}
        spaceId={spaceId}
        activeTab="video"
      >
        {/* Video-specific preview body — full-width frame + controls */}
        <div className={styles.savedVideoFull}>
          <div className={styles.videoThumbWrap} style={{ cursor: 'default' }}>
            {(previewUrl || existingVideoUrl) && (
              <video
                className={styles.reviewVideo}
                src={previewUrl || existingVideoUrl}
                poster={existingPosterUrl || undefined}
                muted
                playsInline
                preload="auto"
                onLoadedData={(e) => {
                  if (e.target.currentTime === 0) e.target.currentTime = 0.1;
                }}
              />
            )}
          </div>
          <span className={styles.savedFullDuration}>{savedDuration}</span>
        </div>
      </SuccessScreen>
    );
  }

  // ─── 4. Review/Save — after recording, library selection, or edit mode ─

  if ((recordingState === 'stopped' && videoBlob) || libraryBlob || (editMode && !saved)) {
    // Determine the video source for the review player
    const reviewSrc = previewUrl || existingVideoUrl || null;
    const reviewPoster = existingPosterUrl || undefined;
    const isEditWithoutNewRecording = editMode && !videoBlob && !libraryBlob;
    return (
      <div className={styles.page}>
        <LovedOneBar
          spaceName={spaceName}
          spacePhotoUrl={spacePhotoUrl}
          subtitle={editMode ? 'Edit video' : 'Review before saving'}
          onBack={editMode ? () => navigate(`/spaces/${spaceId}`, { replace: true }) : handleReRecordOrChoose}
          backLabel={editMode ? 'Back to feed' : 'Back to recording'}
        />

        {prompt && (
          <div className={styles.promptWrap}>
            <PromptBanner prompt={prompt} showSkip={false} fullWidth />
          </div>
        )}

        <div className={styles.reviewBody}>
          {/* Video playback card — custom player (no native controls) */}
          <div className={styles.playbackCard}>
            <span className={styles.playbackLabel}>YOUR VIDEO</span>

            {/* Video thumbnail / frame — click to play/pause */}
            <div className={styles.videoThumbWrap} onClick={toggleVideoPlayback}>
              {reviewSrc && (
                <video
                  ref={reviewVideoRef}
                  className={styles.reviewVideo}
                  src={reviewSrc}
                  poster={reviewPoster}
                  playsInline
                  preload="auto"
                />
              )}
            </div>

            {/* Custom controls: play button + progress bar + duration */}
            <div className={styles.videoControlsRow}>
              <button
                className={`${styles.playBtn} ${videoPlaying ? styles.playBtnPlaying : ''}`}
                onClick={toggleVideoPlayback}
                aria-label={videoPlaying ? 'Pause video' : 'Play video'}
              >
                {videoPlaying ? (
                  <svg width="12" height="14" viewBox="0 0 12 14" fill="white">
                    <rect x="0" y="0" width="4" height="14" rx="1" />
                    <rect x="8" y="0" width="4" height="14" rx="1" />
                  </svg>
                ) : (
                  <svg width="12" height="14" viewBox="0 0 12 14" fill="white">
                    <path d="M0 0l12 7-12 7V0z" />
                  </svg>
                )}
              </button>

              <div
                className={styles.progressTrack}
                onClick={handleProgressClick}
                role="slider"
                aria-label="Video progress"
                aria-valuenow={Math.round(videoProgress)}
                aria-valuemin={0}
                aria-valuemax={100}
                tabIndex={0}
              >
                <div
                  className={styles.progressFill}
                  style={{ width: `${videoProgress}%` }}
                />
              </div>

              <span className={styles.playbackDuration}>
                {videoCurrentTime} / {activeDurationFormatted}
              </span>
            </div>
          </div>

          {/* Settings card — caption/label + privacy grouped */}
          <div className={styles.settingsCard}>
            <div className={styles.settingsInputRow}>
              <input
                type="text"
                className={styles.labelInput}
                placeholder="Caption (optional) — e.g., Advice, Funny moments..."
                value={memoryLabel}
                onChange={(e) => setMemoryLabel(e.target.value)}
              />
            </div>
            <div className={styles.settingsDivider} />
            <div
              className={styles.settingsRow}
              onClick={() => setIsPrivate((prev) => !prev)}
              role="switch"
              aria-checked={isPrivate}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsPrivate((prev) => !prev);
                }
              }}
            >
              <div className={styles.privacyLabel}>
                <span className={styles.privacyIcon}>
                  {isPrivate ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <rect x="5" y="11" width="14" height="10" rx="2" />
                      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                  )}
                </span>
                <span className={styles.privacyText}>{isPrivate ? 'Private' : 'Shared'}</span>
              </div>
              <div className={`${styles.toggleTrack} ${isPrivate ? styles.toggleTrackOn : styles.toggleTrackOff}`}>
                <div className={styles.toggleThumb} />
              </div>
            </div>
          </div>

          <p className={styles.settingsHint}>You can change privacy anytime</p>

          {uploadError && (
            <p className={styles.uploadError} role="alert">{uploadError}</p>
          )}

          {/* Action buttons */}
          <div className={styles.reviewActions}>
            <button className={styles.saveBtn} onClick={editMode ? handleEditSave : handleSave}>
              {editMode ? 'Save changes' : `Add to ${spaceName}\u2019s space`}
            </button>
            <button className={styles.reRecordBtn} onClick={handleReRecordOrChoose}>
              {editMode && isEditWithoutNewRecording ? 'Record new version' :
               libraryBlob && !videoBlob ? 'Choose different video' : 'Re-record'}
            </button>
            {/* v1.4: Delete memory — red text link, edit view only */}
            {editMode && (
              <button
                className={styles.deleteMemoryBtn}
                onClick={handleDelete}
                disabled={deleting}
              >
                Remove memory
              </button>
            )}
          </div>
        </div>

        {/* v1.4: Delete confirmation modal */}
        <DeleteConfirmModal
          open={showDeleteConfirm}
          title="Remove this memory?"
          message="This memory will move to Recently Deleted. You can bring it back anytime in the next 30 days."
          confirmLabel="Remove memory"
          cancelLabel="Keep memory"
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          loading={deleting}
        />

        <BottomNav spaceId={spaceId} activeTab="video" />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // 5. PRE-FLIGHT + RECORDING SCREEN
  // ═══════════════════════════════════════════════════════════

  const isRecording = recordingState === 'recording' || recordingState === 'paused';

  return (
    <div className={styles.page}>
      <LovedOneBar
        spaceName={spaceName}
        spacePhotoUrl={spacePhotoUrl}
        subtitle={barSubtitle}
        onBack={handleBackArrow}
        backLabel="Back to feed"
      />

      {prompt && (
        <div className={styles.promptWrap}>
          <PromptBanner
            prompt={prompt}
            onSkip={handleSkipPrompt}
            showSkip={recordingState === 'idle'}
            fullWidth
          />
        </div>
      )}

      <div className={styles.recordingContent}>
        {/* ─── IDLE: Sage circle + entry buttons (no camera yet) ─── */}
        {recordingState === 'idle' && !previewStream && (
          <div className={styles.circleArea}>
            <button
              className={styles.circle}
              onClick={startRecording}
              aria-label="Start recording video"
            >
              <div className={styles.ring} />
              <div className={styles.circleIcon}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="5" width="14" height="14" rx="2" stroke="var(--color-sage-dark, #5b7a65)" strokeWidth="1.5" />
                  <path d="M16 9.5l6-3.5v12l-6-3.5" stroke="var(--color-sage-dark, #5b7a65)" strokeWidth="1.5" />
                  <circle cx="9" cy="12" r="2" fill="var(--color-sage-dark, #5b7a65)" />
                </svg>
              </div>
            </button>

            <div className={styles.statusArea}>
              <span className={styles.stateLabel}>Ready</span>
              <p className={styles.hint}>Tap the circle to start · 5:00 max</p>
            </div>

            {(recorderError || libraryError) && (
              <p className={styles.recorderError} role="alert">
                {recorderError || libraryError}
              </p>
            )}

            {permissionState === 'denied' && (
              <p className={styles.recorderError} role="alert">
                Camera access denied. Please allow access in your browser settings.
              </p>
            )}

            <div className={styles.entryButtons}>
              <button className={styles.libraryBtn} onClick={handleLibraryClick}>
                Choose from Library
              </button>
            </div>
          </div>
        )}

        {/* ─── CAMERA ACTIVE: warm-bordered preview + controls ─── */}
        {(previewStream || isRecording) && (
          <>
            <div className={styles.videoPreviewArea}>
              <video
                ref={liveVideoRef}
                className={`${styles.liveVideo} ${mirrorClass}`}
                autoPlay
                muted
                playsInline
              />

              {/* Camera toggle — only when not recording */}
              {!isRecording && (
                <button
                  className={styles.cameraToggleBtn}
                  onClick={toggleCamera}
                  aria-label="Switch camera"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 16v4a2 2 0 0 1-2 2h-4" />
                    <path d="M14 2h4a2 2 0 0 1 2 2v4" />
                    <path d="M4 8V4a2 2 0 0 1 2-2h4" />
                    <path d="M10 22H6a2 2 0 0 1-2-2v-4" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              )}

              {/* Recording time + warning overlay */}
              {isRecording && (
                <div className={styles.recordingOverlay}>
                  <div className={`${styles.recordingTimer} ${atWarning ? styles.recordingTimerWarning : ''}`}>
                    <span className={styles.recDot} />
                    <span>{durationFormatted}</span>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.controlsArea}>
              {recorderError && (
                <p className={styles.recorderError} role="alert">{recorderError}</p>
              )}

              {/* Idle with preview: start + library */}
              {recordingState === 'idle' && (
                <div className={styles.entryButtons}>
                  <button className={styles.startRecordBtn} onClick={startRecording}>
                    <span className={styles.startRecordDot} />
                    Start Recording
                  </button>
                  <button className={styles.libraryBtn} onClick={handleLibraryClick}>
                    Choose from Library
                  </button>
                </div>
              )}

              {/* Recording / paused: pause/resume + stop */}
              {isRecording && (
                <>
                  <div className={styles.recordingControls}>
                    <button
                      className={styles.pauseResumeBtn}
                      onClick={handleRecordTap}
                      aria-label={recordingState === 'recording' ? 'Pause recording' : 'Resume recording'}
                    >
                      {recordingState === 'recording' ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <rect x="6" y="4" width="4" height="16" rx="1" />
                          <rect x="14" y="4" width="4" height="16" rx="1" />
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 4l14 8-14 8V4z" />
                        </svg>
                      )}
                      {recordingState === 'recording' ? 'Pause' : 'Resume'}
                    </button>

                    <button
                      className={styles.stopBtn}
                      onClick={stopRecording}
                      aria-label="Stop recording"
                    >
                      <span className={styles.stopIcon} />
                      Stop
                    </button>
                  </div>

                  <div className={styles.statusArea}>
                    <span className={styles.stateLabel}>{stateLabel}</span>
                    <p className={styles.hint}>{hintText}</p>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Hidden file input for library picker */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_VIDEO_EXTENSIONS}
        className={styles.fileInput}
        onChange={handleFileSelected}
      />

      {/* Leave-without-saving modal */}
      <LeaveConfirmModal
        open={showLeaveConfirm}
        message="Your video won't be saved if you leave now."
        icon="record"
        onKeepEditing={() => setShowLeaveConfirm(false)}
        onLeave={() => {
          setShowLeaveConfirm(false);
          navigate(`/spaces/${spaceId}`, { replace: true });
        }}
      />

      <BottomNav spaceId={spaceId} activeTab="video" />
    </div>
  );
}
